import React, { useState, useRef, useEffect } from 'react';
import {
  ConciergeBell,
  Utensils,
  Sparkles,
  Wifi,
  ShowerHead,
  BatteryCharging,
  Luggage,
  Camera,
  Flame,
  Thermometer,
  ChefHat,
  Phone,
  Sprout
} from 'lucide-react';

const facilities = [
  { icon: <ConciergeBell size={32} />, title: '24/7 Front Desk / Reception' },
  { icon: <Utensils size={32} />, title: 'Room Service' },
  { icon: <Sparkles size={32} />, title: 'Housekeeping (Daily Cleaning)' },
  { icon: <Wifi size={32} />, title: 'Free Wi-Fi' },
  { icon: <ShowerHead size={32} />, title: 'Hot & Cold Running Water' },
  { icon: <BatteryCharging size={32} />, title: 'Power Backup' },
  { icon: <Luggage size={32} />, title: 'Luggage Storage' },
  { icon: <Camera size={32} />, title: 'CCTV Security' },
  { icon: <Flame size={32} />, title: 'Fire Safety Systems' },
  { icon: <Thermometer size={32} />, title: 'Room heater (chargeable)' },
  { icon: <ChefHat size={32} />, title: 'Restaurant (room service only)' },
  { icon: <Phone size={32} />, title: 'Intercom' },
  { icon: <Sprout size={32} />, title: 'Garden / Outdoor Seating' },
];

const FacilityCard = ({ icon, title, description, index }) => (
  <article className="facility-card group relative" data-aos="fade-up">
    <div className="icon-wrapper">{icon}</div>
    <span className="badge">{String(index + 1).padStart(2, '0')}</span>
    <h3>{title}</h3>
    <p>{description}</p>
  </article>
);

export default function FacilitiesSection() {
  const [isMobile, setIsMobile] = useState(false);

  // Mobile-specific refs and state
  const mobileRailRef = useRef(null);
  const [mobileActiveIndex, setMobileActiveIndex] = useState(0);

  // Desktop-specific refs and state
  const desktopRailRef = useRef(null);
  const [desktopActiveIndex, setDesktopActiveIndex] = useState(0);

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Mobile Intersection Observer
  useEffect(() => {
    if (!isMobile || !mobileRailRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index, 10);
            setMobileActiveIndex(index);
          }
        });
      },
      {
        root: mobileRailRef.current,
        threshold: 0.5,
      }
    );

    const cards = mobileRailRef.current.children;
    Array.from(cards).forEach((card) => observer.observe(card));

    return () => {
      Array.from(cards).forEach((card) => observer.unobserve(card));
    };
  }, [isMobile]);

  const handleMobileDotClick = (index) => {
    if (mobileRailRef.current) {
      const cardElement = mobileRailRef.current.children[index];
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }
  };

  const handleDesktopDotClick = (index) => {
    if (desktopRailRef.current) {
      desktopRailRef.current.scrollTo({ left: index * desktopRailRef.current.clientWidth, behavior: 'smooth' });
    }
  };

  const handleDesktopScroll = () => {
    if (desktopRailRef.current) {
      const scrollLeft = desktopRailRef.current.scrollLeft;
      const clientWidth = desktopRailRef.current.clientWidth;
      const newIndex = Math.round(scrollLeft / clientWidth);
      if (newIndex !== desktopActiveIndex) {
        setDesktopActiveIndex(newIndex);
      }
    }
  };

  const slides = [];
  for (let i = 0; i < facilities.length; i += 6) { // Group by 6 for 2 rows, 3 columns
    slides.push(facilities.slice(i, i + 6));
  }

  return (
    <section className="section facilities-section bg-beige" aria-label="Facilities">
      <div className="container">
        <div className="eyebrow" data-aos="fade-up"><span className="dot"></span><span>Happy to provide</span></div>
        <h2 className="title" data-aos="fade-up">Facilities</h2>
        <p className="lead" data-aos="fade-up">At Pineridge, enjoy modern comforts blended with traditional charm. Our facilities include well‑appointed rooms, an in‑house multi‑cuisine restaurant, high‑speed Wi‑Fi, concierge, 24/7 front desk service, and cozy lounges—all designed to make your stay seamless, relaxing, and truly memorable.</p>

        {isMobile ? (
          <>
            <div className="facilities-grid-container" ref={mobileRailRef}>
              {facilities.map((facility, index) => (
                <div data-index={index} key={index} className="facility-card-wrapper">
                  <FacilityCard {...facility} index={index} />
                </div>
              ))}
            </div>
            <div className="facilities-dots">
              {facilities.map((_, index) => (
                <button
                  key={index}
                  className={mobileActiveIndex === index ? 'active' : ''}
                  onClick={() => handleMobileDotClick(index)}
                  aria-label={`Go to facility ${index + 1}`}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="rail" ref={desktopRailRef} onScroll={handleDesktopScroll}>
              {slides.map((slide, slideIndex) => (
                <div className="slide" key={slideIndex}>
                  <div className="grid">
                    {slide.map((facility, index) => (
                      <FacilityCard key={index} {...facility} index={slideIndex * 6 + index} />
                    ))}
                    {slide.length < 6 && Array(6 - slide.length).fill(0).map((_, i) => <div key={i} className="card-placeholder" />)}
                  </div>
                </div>
              ))}
            </div>
            <div className="dots" aria-hidden="false">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`${desktopActiveIndex === index ? 'active' : ''} hover:scale-[1.2] transition`}
                  onClick={() => handleDesktopDotClick(index)}
                  aria-label={`Slide ${index + 1}`}
                ></button>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}